import { FC, useState } from 'react';

import { push } from 'connected-react-router';
import { NON_BREAKING_SPACE, SPACE } from 'utils/constants/common';
import { MagritteNotificationName } from 'utils/constants/notification';
import { conversion } from 'utils/conversion';

import multicreationBackButtonClick from '@hh.ru/analytics-js-events/build/clickme/employer/auction/multicreation/multicreation_back_button_click';
import { ActionBar, Button, ProgressBar, Text, useBreakpoint, VSpacing } from '@hh.ru/magritte-ui';

import useTrls from 'hooks/translation/useTrls';
import { useDashboardRoute } from 'hooks/useDashboardRoute';

import { fetchVacanciesAuctionInfo } from 'components/MultiCreationAuction/api/fetchVacanciesAuctionInfo';
import { MAX_VACANCY_SELECT } from 'components/MultiCreationAuction/VacanciesTable/VacanciesTable';

import { MultiCreationAuctionSteps } from 'store/models/multiCreation';
import { addMagritteNotification } from 'store/models/notification';
import { useDispatch, useSelector } from 'store/utils';

import styles from 'components/MultiCreationAuction/VacanciesTable/VacanciesTableBottomPanel/VacanciesTableBottomPanel.module.less';

const TrlKeys = {
    cancel: 'MultiCreationAuction.cancel',
    next: 'MultiCreationAuction.next',
    selected: {
        default: 'MultiCreationAuction.VacanciesTable.selectedVacanciesCount.selected.default',
        one: 'MultiCreationAuction.VacanciesTable.selectedVacanciesCount.selected.one',
    },
    vacancies: {
        zero: 'MultiCreationAuction.VacanciesTable.selectedVacanciesCount.vacancies.zero',
        one: 'MultiCreationAuction.VacanciesTable.selectedVacanciesCount.vacancies.one',
        some: 'MultiCreationAuction.VacanciesTable.selectedVacanciesCount.vacancies.some',
        many: 'MultiCreationAuction.VacanciesTable.selectedVacanciesCount.vacancies.many',
    },
};

export const VacanciesTableBottomPanel: FC = () => {
    const selectedVacancyIds = useSelector(({ multiCreation }) => multiCreation.selectedVacancyIds);
    const [isLoading, setIsLoading] = useState(false);
    const isDynamicPriceEnabled = useSelector(({ user }) => user.user?.currentAccount.dynamicPriceEnabled);

    const { isMobile } = useBreakpoint();

    const dispatch = useDispatch();
    const trls = useTrls();
    const dashboardRoute = useDashboardRoute();

    const selectedVacanciesCount = Object.keys(selectedVacancyIds).length;

    const progressBar = <ProgressBar progress={1} segments={isDynamicPriceEnabled ? 3 : 2} />;

    const handleNextClick = () => {
        if (selectedVacanciesCount === 0 || selectedVacanciesCount > MAX_VACANCY_SELECT) {
            dispatch(addMagritteNotification(MagritteNotificationName.MultiCreationAuctionIncorrectCount));
            return;
        }
        void dispatch(fetchVacanciesAuctionInfo(Object.keys(selectedVacancyIds), setIsLoading));
    };

    const handleCancelClick = () => {
        dispatch(push(dashboardRoute));
        multicreationBackButtonClick({ currentStep: MultiCreationAuctionSteps.VacanciesSelectStep });
    };

    return (
        <>
            <ActionBar
                type={isMobile ? 'vertical' : 'horizontal'}
                isSticky
                showProgress={progressBar}
                primaryActions={
                    <Button
                        data-qa="next-button"
                        mode="primary"
                        style="accent"
                        onClick={handleNextClick}
                        loading={isLoading}
                    >
                        {trls(TrlKeys.next)}
                    </Button>
                }
                additionalContent={
                    <div className={styles.additionalContent}>
                        {isMobile && (
                            <>
                                <div className={styles.progressBar}>{progressBar}</div>
                                <VSpacing default={8} />
                            </>
                        )}
                        <Text typography="label-1-regular" data-qa="selected-vacancies-count">
                            {conversion(selectedVacanciesCount, {
                                zero: trls(TrlKeys.selected.default),
                                one: trls(TrlKeys.selected.one),
                                some: trls(TrlKeys.selected.default),
                                many: trls(TrlKeys.selected.default),
                            })}
                            {SPACE}
                            {selectedVacanciesCount}
                            {NON_BREAKING_SPACE}
                            {conversion(selectedVacanciesCount, {
                                zero: trls(TrlKeys.vacancies.zero),
                                one: trls(TrlKeys.vacancies.one),
                                some: trls(TrlKeys.vacancies.some),
                                many: trls(TrlKeys.vacancies.many),
                            })}
                        </Text>
                    </div>
                }
                secondaryActions={
                    <Button data-qa="cancel-button" mode="secondary" style="accent" onClick={handleCancelClick}>
                        {trls(TrlKeys.cancel)}
                    </Button>
                }
            />
        </>
    );
};
